class ProductBundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.data = JSON.parse(this.querySelector('[data-bundle-data]').textContent);
    
    // --- FIX: Overwrite raw prices with the rounded market prices ---
    this.data.components.forEach(comp => {
        if (comp.price_formatted) {
            const parsed = this.parseFormattedPrice(comp.price_formatted);
            // Only overwrite if we successfully parsed a valid positive number
            if (parsed > 0) {
                comp.price = parsed;
            }
        }
    });
    // ------------------------------------------------------------

    this.defaultSetContainer = this.querySelector('[data-default-set-container]');
    this.componentsContainer = this.querySelector('[data-components-container]');
    this.customAtcContainer = this.querySelector('[data-custom-atc-container]');

    this.state = {
      isFullSetMode: true,
      selectedItems: [],
    };

    this.defaultSet = this.data.rules.find(rule => rule.is_default === true);
    if (this.defaultSet) {
       this.state.selectedItems = this.defaultSet.component_variant_ids.map(id => ({ variant_id: id }));
    }
  }

  // --- NEW HELPER METHOD ---
  parseFormattedPrice(str) {
    if (!str) return 0;
    
    // Handle formats like "1.234,56" (DE) and "1,234.56" (US/IN)
    let s = str.trim();
    
    // Check for decimals: looks for a separator (. or ,) followed by 1 or 2 digits at the end
    const decimalMatch = s.match(/([.,])(\d{1,2})$/);
    
    if (decimalMatch) {
       const cents = decimalMatch[2];
       
       // Remove the cents part from the string
       let mainPart = s.substring(0, s.length - (cents.length + 1));
       
       // Remove ALL non-digits from the integer part (removes thousands separators)
       mainPart = mainPart.replace(/[^0-9]/g, '');
       
       // Reassemble as a standard float number
       const combined = mainPart + "." + cents;
       return Math.round(parseFloat(combined) * 100);
    } 
    
    // Fallback for currencies without decimals (like JPY) or clean integers
    // Return 0 so the constructor keeps the original raw price
    return 0; 
  }

  connectedCallback() {
    this.render();
    this.initEventListeners();
    this.update();
    this.updateImages();
  }

  render() {
    // This is the "Full Set" button that also acts as a state selector
    const defaultRule = this.data.rules.find(rule => rule.is_default === true);
    if (defaultRule) {
      this.defaultSetContainer.innerHTML = `
        <button type="button" class="bundle-button bundle-button--full-set" data-action="select-full-set">
          <span class="bundle-full-set__info">
            <span class="bundle-full-set__title">The Full Moulin Set</span>
            <span class="bundle-full-set__includes">Includes Salt/Pepper mills and caddy</span>
          </span>
          <span class="bundle-full-set__price" data-full-set-price>${this.formatMoney(this.calculatePrice(this.defaultSet.component_variant_ids, defaultRule))}</span>
        </button>
      `;
    }

    this.componentsContainer.insertAdjacentHTML('beforebegin', `
      <div class="bundle-customizer-title">
        <h3 class="bundle-title">Don’t need the full set?</h3>
        <p>Configure your own setup below:</p>
      </div>
    `);
    
    let componentsHtml = '';
    this.data.components.forEach(item => {
      componentsHtml += `<button type="button" class="bundle-button bundle-button--component" role="checkbox" aria-checked="false" data-variant-id="${item.variant_id}" data-action="toggle-item">${item.title}</button>`;
    });
    this.componentsContainer.innerHTML = componentsHtml;
    
    this.customAtcContainer.innerHTML = `
      <button type="button" class="bundle-button bundle-button--add-to-cart" data-action="add-custom-to-cart">
        <span data-custom-atc-text>Custom Bundle Total:</span>
        <span data-custom-price>${this.formatMoney(0)}</span>
      </button>
    `;

    this.fullSetButton = this.querySelector('[data-action="select-full-set"]');
    this.componentButtons = this.querySelectorAll('.bundle-button--component');
    this.customAtcButton = this.querySelector('[data-action="add-custom-to-cart"]');
    this.atcText = this.querySelector('[data-custom-atc-text]');
    this.atcPrice = this.querySelector('[data-custom-price]');
  }

  
  // REPLACE this entire method in your file
  initEventListeners() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (!button) return;
        const { action } = button.dataset;

        // This is a flag to see if we need to call update() at the end
        let shouldUpdate = true;

        // Clear selection when switching from full set mode to custom
        if (this.state.isFullSetMode && action === 'toggle-item') {
          this.state.selectedItems = [];
        }

        switch (action) {
          case 'select-full-set':
            if (this.state.isFullSetMode) {
              // If already in full set mode, this click IS an "Add to Cart" action.
              this.addToCart('full_set', button);
              shouldUpdate = false; // addToCart will handle its own update
            } else {
              // If in custom mode, this click is a RESET to the default state.
              this.state.isFullSetMode = true;
              this.state.selectedItems = this.defaultSet ? this.defaultSet.component_variant_ids.map(id => ({ variant_id: id })) : [];
              this.updateImages();
            }
            break;
          case 'toggle-item': {
            this.state.isFullSetMode = false;
            const variantId = parseInt(button.dataset.variantId, 10);
            const itemIndex = this.state.selectedItems.findIndex(item => item.variant_id === variantId);
            if (itemIndex > -1) {
              this.state.selectedItems.splice(itemIndex, 1);
            } else {
              this.state.selectedItems.push({ variant_id: variantId });
            }
            this.updateImages();
            break;
          }
          case 'add-custom-to-cart':
            this.addToCart('custom', button);
            shouldUpdate = false; // addToCart will handle its own update
            break;
        }

        // Only call update if a state-changing action was taken, but not an add-to-cart action.
        if (shouldUpdate) {
          this.update();
        }
      });
  }

  update() {
    this.fullSetButton?.classList.toggle('is-active', this.state.isFullSetMode);
    this.fullSetButton?.classList.toggle('strikethrough', !this.state.isFullSetMode);

    const selectedIds = new Set(this.state.selectedItems.map(item => item.variant_id));

    this.componentButtons.forEach(button => {
      const variantId = parseInt(button.dataset.variantId, 10);
      const isActive = !this.state.isFullSetMode && selectedIds.has(variantId);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-checked', isActive.toString());
    });

    let finalPrice = 0;

    if (this.state.isFullSetMode && this.defaultSet) {
      // Full Set Mode: Direct Price
      finalPrice = this.defaultSet.bundle_price 
                   ? this.defaultSet.bundle_price 
                   : this.calculatePrice(this.defaultSet.component_variant_ids, this.defaultSet);
    } else {
      
      // --- PASS 1: Calculate "Physical Cart Price" (What actually gets added) ---
      let physicalPrice = 0;
      let alreadyAppliedDiscountUSD = 0;
      
      // Clone the list of selected items to consume them
      let remainingForPhysical = this.state.selectedItems.map(item => item.variant_id);

      while (remainingForPhysical.length > 0) {
        const remainingSet = new Set(remainingForPhysical);

        // Find rules that have a REAL Shopify Variant ID (bundle_variant_id)
        const physicalRules = this.data.rules.filter(rule => 
            rule.bundle_variant_id && 
            rule.component_variant_ids.every(id => remainingSet.has(id))
        );
        
        // Sort: Largest bundle first (Greedy)
        physicalRules.sort((a, b) => b.component_variant_ids.length - a.component_variant_ids.length);

        if (physicalRules.length > 0) {
          const bestRule = physicalRules[0];
          
          // Add the Physical Variant Price (e.g., 5300000)
          physicalPrice += bestRule.bundle_price ? bestRule.bundle_price : 0;
          
          // Record that we have already used up $125 of discount here
          alreadyAppliedDiscountUSD += bestRule.discount;

          // Remove these items from the list
          const usedIds = new Set(bestRule.component_variant_ids);
          const newRemaining = [];
          const removed = new Set();
          for (const id of remainingForPhysical) {
            if (usedIds.has(id) && !removed.has(id)) {
              removed.add(id);
            } else {
              newRemaining.push(id);
            }
          }
          remainingForPhysical = newRemaining;
        } else {
          // No bundle fits, just add the single item price (e.g., 2920000)
          const currentId = remainingForPhysical[0];
          const component = this.data.components.find(c => c.variant_id === currentId);
          physicalPrice += (component ? component.price : 0);
          
          remainingForPhysical.shift(); // Remove the single item
        }
      }

      // --- PASS 2: Calculate "Target Discount" (What the App/Function wants to give) ---
      let totalTargetDiscountUSD = 0;
      let remainingForDiscount = this.state.selectedItems.map(item => item.variant_id);

      while (remainingForDiscount.length > 0) {
        const remainingSet = new Set(remainingForDiscount);

        // Look at ALL rules (Virtual OR Physical) to find the best deal logic
        const allRules = this.data.rules.filter(rule => 
            rule.component_variant_ids.every(id => remainingSet.has(id))
        );

        // Sort: Largest size first, then biggest discount
        allRules.sort((a, b) => {
            if (b.component_variant_ids.length !== a.component_variant_ids.length) {
                return b.component_variant_ids.length - a.component_variant_ids.length;
            }
            return b.discount - a.discount;
        });

        if (allRules.length > 0) {
            const bestRule = allRules[0];
            
            // This combination deserves this much discount (e.g., $185)
            totalTargetDiscountUSD += bestRule.discount;

            // Remove items
            const usedIds = new Set(bestRule.component_variant_ids);
            const newRemaining = [];
            const removed = new Set();
            for (const id of remainingForDiscount) {
                if (usedIds.has(id) && !removed.has(id)) {
                    removed.add(id);
                } else {
                    newRemaining.push(id);
                }
            }
            remainingForDiscount = newRemaining;
        } else {
            remainingForDiscount.shift();
        }
      }

      // --- PASS 3: Calculate the Difference ---
      // Target ($185) - Already Applied ($125) = Extra Discount Needed ($60)
      const extraDiscountUSD = Math.max(0, totalTargetDiscountUSD - alreadyAppliedDiscountUSD);
      
      let extraDiscountCents = 0;
      
      // Convert that $60 USD to INR (or current currency)
      const currencyCode = this.data.currency ? this.data.currency.iso_code : 'USD';
      if (currencyCode !== 'USD' && window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
          const rate = parseFloat(window.Shopify.currency.rate);
          // $60 * 91.33 * 100 = ~547,980 cents
          extraDiscountCents = Math.round(extraDiscountUSD * rate * 100);
      } else {
          extraDiscountCents = extraDiscountUSD * 100;
      }

      // Final Price = Physical Price - Extra Discount
      finalPrice = Math.max(0, physicalPrice - extraDiscountCents);
    }
    
    this.atcPrice.textContent = this.formatMoney(finalPrice);
    this.customAtcButton.disabled = this.state.selectedItems.length === 0;
  }

  calculatePrice(componentIds, rule) {
    // Calculate base price of components
    const basePrice = componentIds.reduce((total, id) => {
        const component = this.data.components.find(c => c.variant_id === id);
        return total + (component ? component.price : 0);
    }, 0);
    
    if (rule && typeof rule.discount === 'number') {
        let discountInCents = 0;
        
        // Check current currency context
        const currencyCode = this.data.currency ? this.data.currency.iso_code : 'USD';
        
        // If the currency is NOT USD, we assume the discount (125) is USD and needs conversion.
        // We check for window.Shopify.currency.rate (Base -> Active Rate).
        if (currencyCode !== 'USD' && window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) {
            const rate = parseFloat(window.Shopify.currency.rate);
            if (!isNaN(rate)) {
                // Formula: 125 USD * Rate (91.33) * 100 (Cents)
                discountInCents = Math.round(rule.discount * rate * 100);
            } else {
                // Fallback if rate is invalid
                discountInCents = rule.discount * 100;
            }
        } else {
            // USD context: 125 * 100 = 12500 cents
            discountInCents = rule.discount * 100;
        }
        
        return Math.max(0, basePrice - discountInCents);
    }
    return basePrice;
  }

  updateImages() {
    const productImages = document.querySelectorAll('[data-prod-img]');
    let handle = '';
    let selectedTitles = [];

    if (this.state.isFullSetMode) {
        selectedTitles = this.defaultSet.component_variant_ids.map(id => {
            const component = this.data.components.find(c => c.variant_id === id);
            return component ? component.title : '';
        }).filter(Boolean);
    } else {
        selectedTitles = this.state.selectedItems.map(item => {
            const component = this.data.components.find(c => c.variant_id === item.variant_id);
            return component ? component.title : '';
        }).filter(Boolean);
    }

    if (selectedTitles.length > 0) {
        handle = `[${selectedTitles.map(title => this.handleize(title)).sort().join('-')}]`;
    } else {
        // If no selection, revert to default set image handle
        const defaultTitles = this.defaultSet.component_variant_ids.map(id => {
            const component = this.data.components.find(c => c.variant_id === id);
            return component ? component.title : '';
        }).filter(Boolean);
        handle = `[${defaultTitles.map(title => this.handleize(title)).sort().join('-')}]`;
    }
    console.log('Selected handle:', handle);

    let matchFound = false;
    productImages.forEach(img => {
        const imgAltTag = img.dataset.prodImg;
        if (imgAltTag && imgAltTag === handle) {
            img.classList.remove('is-hidden');
            matchFound = true;
        } else if (imgAltTag) {
            img.classList.add('is-hidden');
        }
    });

    // If no specific match, show images without any data-prod-img tag
    if (!matchFound) {
        productImages.forEach(img => {
            if (!img.dataset.prodImg) {
                img.classList.remove('is-hidden');
            } else {
                img.classList.add('is-hidden');
            }
        });
    }

    // Re-initialize slider
    const slider = document.querySelector('slider-component');
    if (slider && typeof slider.initPages === 'function') {
        setTimeout(() => {
            slider.initPages();
        }, 100);
    }
  }

  addToCart(mode, button) {
    let itemsToAdd = [];
    
    // Determine items to add based on the button that was clicked, not the mode
    if (button.dataset.action === 'add-full-set-to-cart' || (this.state.isFullSetMode && button.dataset.action !== 'add-custom-to-cart')) {
        const defaultRule = this.data.rules.find(rule => rule.is_default === true);
        if (defaultRule && defaultRule.bundle_variant_id) {
            itemsToAdd.push({ id: defaultRule.bundle_variant_id, quantity: 1 });
        }
    } else { // Custom add to cart
        let remainingIds = this.state.selectedItems.map(item => item.variant_id);
        
        // Greedy approach: Find the largest matching SKU Swap rule
        while (remainingIds.length > 0) {
            const remainingSet = new Set(remainingIds);
            
            // Find all SKU Swap rules that are subsets of remainingIds
            const validRules = this.data.rules.filter(rule => 
                rule.action_type === 'SKU Swap' && 
                rule.bundle_variant_id &&
                rule.component_variant_ids.every(id => remainingSet.has(id))
            );

            // Sort by size (descending) to get the largest bundle first
            validRules.sort((a, b) => b.component_variant_ids.length - a.component_variant_ids.length);

            if (validRules.length > 0) {
                const bestRule = validRules[0];
                itemsToAdd.push({ id: bestRule.bundle_variant_id, quantity: 1 });
                
                // Remove used IDs
                const usedIds = new Set(bestRule.component_variant_ids);
                // Only remove one instance of each used ID
                const newRemaining = [];
                const removed = new Set();
                for (const id of remainingIds) {
                    if (usedIds.has(id) && !removed.has(id)) {
                        removed.add(id);
                    } else {
                        newRemaining.push(id);
                    }
                }
                remainingIds = newRemaining;
            } else {
                // No more bundles fit, add remaining as individual items
                remainingIds.forEach(id => itemsToAdd.push({ id: id, quantity: 1 }));
                remainingIds = [];
            }
        }
    }

    if (itemsToAdd.length === 0) return;
    
    button.classList.add('is-loading');
    button.disabled = true;

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: itemsToAdd }),
    })
    .then(response => response.json())
    .then(item => fetchCart().then(cart => {
      let cartDrawer = document.querySelector("cart-drawer");
      if (cartDrawer) {
        cartDrawer.open(cart);
        const cartUpdateEvent = new CustomEvent("cart:updated", { bubbles: true, detail: { cart } });
        document.dispatchEvent(cartUpdateEvent);
      }
    }))
    .catch((error) => console.error('Error:', error))
    .finally(() => {
      button.classList.remove('is-loading');
      button.disabled = false; // Re-enable the clicked button
      this.update();
    });
  }
  
  handleize(str) {
    return str.toLowerCase().replace(/[^a-z0-9\u00C0-\u017F]+/g, '-').replace(/^-+|-+$/g, '');
  }

  formatMoney(cents) {
    if (typeof cents !== 'number') return this.formatMoney(0);
    
    const currencyCode = this.data.currency ? this.data.currency.iso_code : 'USD';
    
    // Check if the amount has cents (e.g., 73190 % 100 = 90)
    const hasCents = cents % 100 !== 0;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', 
        currency: currencyCode, 
        // If it has cents, show 2 decimals. If not, show 0.
        minimumFractionDigits: 0, 
        maximumFractionDigits: 0, 
      }).format(cents / 100);
    } catch (e) {
      console.error('Currency formatting error:', e);
      return '$' + (cents / 100).toFixed(0);
    }
  }

  calculatePrice(componentIds, rule) {
    // 1. "Source of Truth" Check
    // If this rule corresponds to a physical Shopify Variant (like the Full Set),
    // always use that variant's specific price to match Shopify's rounding.
    if (rule && rule.bundle_price) {
        return rule.bundle_price;
    }

    // 2. Standard Calculation (for custom bundles without a physical variant)
    const basePrice = componentIds.reduce((total, id) => {
        const component = this.data.components.find(c => c.variant_id === id);
        return total + (component ? component.price : 0);
    }, 0);
    
    if (rule && typeof rule.discount === 'number') {
        let discountInCents = 0;
        const currencyCode = this.data.currency ? this.data.currency.iso_code : 'USD';
        
        if (currencyCode !== 'USD' && window.Shopify?.currency?.rate) {
            const rate = parseFloat(window.Shopify.currency.rate);
            discountInCents = Math.round(rule.discount * rate * 100);
        } else {
            discountInCents = rule.discount * 100;
        }
        
        return Math.max(0, basePrice - discountInCents);
    }
    
    return basePrice;
  }
}

customElements.define('product-bundle-builder', ProductBundleBuilder);